/**
 * useKeyboardReorderAnnouncement — aria-live announcement for keyboard column reorder.
 *
 * Computes the announced position from the live post-move `columnOrder` rather
 * than the pre-move value captured at call time, so rapid sequential arrow-key
 * presses (faster than a render cycle) each announce their own correct final
 * position instead of a stale one.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { DataTableColumnConfig } from '../types';

function getColId(col: DataTableColumnConfig): string {
  return String(col.name ?? col.kind);
}

export interface UseKeyboardReorderAnnouncementResult {
  /** Current aria-live announcement text (empty string when nothing to announce) */
  reorderAnnouncement: string;
  /** Call before triggering a keyboard-driven reorder for colId */
  notifyKeyboardMove: (colId: string, direction: 'left' | 'right') => void;
}

export function useKeyboardReorderAnnouncement(
  columnOrder: string[],
  rightColumns: DataTableColumnConfig[]
): UseKeyboardReorderAnnouncementResult {
  const [reorderAnnouncement, setReorderAnnouncement] = useState('');

  const pendingColIdRef = useRef<string | null>(null);
  const prevColumnOrderRef = useRef(columnOrder);

  const notifyKeyboardMove = useCallback(
    (colId: string, direction: 'left' | 'right') => {
      // Same boundary check reorderByKeyboard itself does — if the move would
      // be a no-op, skip setting the pending ref. Otherwise a pending colId
      // would linger for a move that never changes columnOrder, and the
      // effect below would never fire to clear it, so it would incorrectly
      // attach to the next unrelated reorder.
      const idx = columnOrder.indexOf(colId);
      if (idx === -1) {
        return;
      }
      const targetIdx = direction === 'left' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= columnOrder.length) {
        return;
      }
      pendingColIdRef.current = colId;
    },
    [columnOrder]
  );

  useEffect(() => {
    const pendingColId = pendingColIdRef.current;
    if (pendingColId !== null && columnOrder !== prevColumnOrderRef.current) {
      const newIdx = columnOrder.indexOf(pendingColId);
      if (newIdx !== -1) {
        const colLabel = String(
          rightColumns.find(c => getColId(c) === pendingColId)?.name ??
            pendingColId
        );
        setReorderAnnouncement(
          `Column ${colLabel} moved to position ${newIdx + 1}`
        );
      }
      pendingColIdRef.current = null;
    }
    prevColumnOrderRef.current = columnOrder;
  }, [columnOrder, rightColumns]);

  // Reset the announcement after a brief delay so re-announcing the same
  // position (e.g. after undo/redo) works.
  useEffect(() => {
    if (!reorderAnnouncement) {
      return;
    }
    const timer = setTimeout(() => setReorderAnnouncement(''), 2000);
    return () => clearTimeout(timer);
  }, [reorderAnnouncement]);

  return { reorderAnnouncement, notifyKeyboardMove };
}
